import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Download, ExternalLink, Gauge, Weight, Phone, ArrowLeft, MessageSquare, Send, X, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, formatINR } from "@/lib/api";
import { mediaUrl, extractYouTubeId } from "@/lib/media";
import { useSettings } from "@/lib/settings";
import { Section } from "@/components/Section";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WhatsAppInquireButton from "@/components/WhatsAppInquireButton";
import EMICalculator from "@/components/EMICalculator";
import MediaLightbox from "@/components/MediaLightbox";

export default function ProductDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [p, setP] = useState(null);
  const [meta, setMeta] = useState({ brands: [] });
  const [err, setErr] = useState("");
  const [activeMedia, setActiveMedia] = useState({ type: "image", src: "" });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);
  const [inquireOpen, setInquireOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", brand: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [brochureOpen, setBrochureOpen] = useState(false);
  const [brochureForm, setBrochureForm] = useState({ name: "", phone: "", email: "" });
  const [brochureSubmitting, setBrochureSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setActiveMedia({ type: "image", src: r.data.image });
      setForm((f) => ({ ...f, brand: r.data.brand }));
    }).catch(() => setErr("Product not found"));
    api.get("/products-meta").then((r) => setMeta(r.data)).catch(() => {});
  }, [slug]);

  // build thumbnail list: primary image + extra images + video (if any)
  const gallery = useMemo(() => {
    if (!p) return [];
    const arr = [{ type: "image", src: p.image }];
    (p.images || []).filter((u) => u && u !== p.image).forEach((src) => arr.push({ type: "image", src }));
    if (p.video_url) {
      const yt = extractYouTubeId(p.video_url);
      arr.push({ type: yt ? "youtube" : "video", src: p.video_url, ytid: yt });
    }
    return arr;
  }, [p]);

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!p) return;
    setSubmitting(true);
    try {
      await api.post("/leads/contact", {
        name: form.name, email: form.email, phone: form.phone,
        subject: `Product Inquiry — ${p.title}`,
        message: `Brand interest: ${form.brand}\nProduct: ${p.title}\n\n${form.message || "Please share more details and best price."}`,
        lead_type: "product_inquiry", product_id: p.id, product_title: p.title, brand: form.brand || p.brand,
      });
      toast.success("Inquiry sent! Our team will call you within 2 hours.");
      setInquireOpen(false);
      setForm({ name: "", email: "", phone: "", brand: p.brand, message: "" });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to submit.");
    } finally { setSubmitting(false); }
  };

  const brochureGated = settings?.brochure_gating_enabled !== false; // default ON

  const onBrochureClick = (e) => {
    if (!p?.brochure_url) return;
    if (!brochureGated) return; // allow direct open
    e.preventDefault();
    setBrochureOpen(true);
  };

  const submitBrochureLead = async (e) => {
    e.preventDefault();
    if (!p) return;
    setBrochureSubmitting(true);
    try {
      await api.post("/leads/contact", {
        name: brochureForm.name,
        email: brochureForm.email || null,
        phone: brochureForm.phone,
        subject: `Brochure Download — ${p.title}`,
        message: `User downloaded the brochure for ${p.title} (${p.brand}). Follow up with EMI / availability.`,
        lead_type: "brochure_download",
        product_id: p.id,
        product_title: p.title,
        brand: p.brand,
      });
      toast.success("Opening brochure… our team will call within 2 hours.");
      setBrochureOpen(false);
      setBrochureForm({ name: "", phone: "", email: "" });
      window.open(mediaUrl(p.brochure_url), "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Submission failed.");
    } finally { setBrochureSubmitting(false); }
  };

  if (err) return (
    <Section className="py-32 text-center">
      <h2 className="font-heading font-black text-3xl">{err}</h2>
      <Link to="/products" className="btn-outline mt-6 inline-flex"><ArrowLeft className="w-4 h-4" /> Back to Catalog</Link>
    </Section>
  );
  if (!p) return <Section className="py-32"><div className="skeleton h-96" /></Section>;

  const ytId = activeMedia.type === "youtube" ? extractYouTubeId(activeMedia.src) : null;

  return (
    <>
      <section className="bg-[#1A1A1A] text-white pt-10 pb-8 noise-overlay">
        <Section>
          <Link to="/products" className="text-xs uppercase tracking-widest text-gray-400 hover:text-[#FFCC00] inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> Back to Catalog</Link>
          <div className="mt-4 label-eyebrow">{p.brand} • {p.category}</div>
          <h1 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-2 text-white" data-testid="product-title">{p.title}</h1>
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <button
            type="button"
            onClick={() => {
              const i = gallery.findIndex((m) => m.src === activeMedia.src && m.type === activeMedia.type);
              setLightboxStart(i >= 0 ? i : 0);
              setLightboxOpen(true);
            }}
            className="aspect-[4/3] w-full bg-gray-100 overflow-hidden border border-gray-200 relative group cursor-zoom-in block"
            data-testid="product-gallery-main"
            aria-label="Open image viewer"
          >
            {activeMedia.type === "image" ? (
              <img src={mediaUrl(activeMedia.src)} alt={p.title} className="w-full h-full object-contain bg-white transition-transform group-hover:scale-[1.02]" />
            ) : activeMedia.type === "youtube" && ytId ? (
              <iframe src={`https://www.youtube.com/embed/${ytId}?autoplay=1`} title={p.title} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
            ) : (
              <video src={mediaUrl(activeMedia.src)} controls autoPlay className="w-full h-full object-contain bg-black" />
            )}
            {activeMedia.type === "image" && (
              <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold opacity-0 group-hover:opacity-100 transition-opacity">
                Click to expand
              </div>
            )}
          </button>
          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3" data-testid="product-gallery-thumbs">
              {gallery.map((m, i) => {
                const active = activeMedia.src === m.src && activeMedia.type === m.type;
                return (
                  <button
                    key={i}
                    onClick={() => setActiveMedia(m)}
                    className={`aspect-square border-2 overflow-hidden relative ${active ? "border-[#E65C00]" : "border-gray-200 hover:border-gray-400"}`}
                    aria-label={`Preview ${i + 1}`}
                    data-testid={`gallery-thumb-${i}`}
                  >
                    {m.type === "image" ? (
                      <img src={mediaUrl(m.src)} alt="" className="w-full h-full object-cover" />
                    ) : m.type === "youtube" ? (
                      <>
                        <img src={`https://img.youtube.com/vi/${m.ytid}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center"><Play className="w-5 h-5 text-white fill-white" /></div>
                      </>
                    ) : (
                      <div className="w-full h-full bg-[#1A1A1A] flex items-center justify-center"><Play className="w-5 h-5 text-white fill-white" /></div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="flex flex-wrap gap-3 mb-6">
            {p.power_hp ? <span className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 text-xs font-bold"><Gauge className="w-3.5 h-3.5 text-[#E65C00]" /> {p.power_hp} HP</span> : null}
            {p.weight ? <span className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 text-xs font-bold"><Weight className="w-3.5 h-3.5 text-[#E65C00]" /> {p.weight}</span> : null}
            {p.capacity ? <span className="inline-flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 text-xs font-bold">{p.capacity}</span> : null}
          </div>

          <p className="mt-6 text-gray-700 leading-relaxed">{p.description}</p>

          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button onClick={() => setInquireOpen(true)} className="btn-primary" data-testid="product-inquire-cta">
              <MessageSquare className="w-4 h-4" /> Inquire Now
            </button>
            <WhatsAppInquireButton itemTitle={p.title} kind="product" className="px-5 py-2.5 text-xs" />
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-black text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
              data-testid="product-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            {p.gem_url && (
              <a href={p.gem_url} target="_blank" rel="noreferrer" className="btn-outline" data-testid="product-gem-cta">
                Inquire via GeM <ExternalLink className="w-4 h-4" />
              </a>
            )}
            {p.brochure_url && (
              <a
                href={brochureGated ? "#" : mediaUrl(p.brochure_url)}
                target={brochureGated ? undefined : "_blank"}
                rel={brochureGated ? undefined : "noreferrer"}
                onClick={onBrochureClick}
                className="btn-yellow"
                data-testid="product-brochure"
              >
                <Download className="w-4 h-4 inline-block mr-1" /> Brochure (PDF)
              </a>
            )}
          </div>
        </div>
      </Section>

      {p.specs && Object.keys(p.specs).length > 0 && (
        <Section className="pb-12">
          <h2 className="heading-xl text-2xl sm:text-3xl mb-6">TECHNICAL SPECIFICATIONS</h2>
          <div className="border border-gray-300">
            <table className="w-full" data-testid="product-specs-table">
              <tbody>
                {Object.entries(p.specs).map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-5 py-3 font-bold text-sm w-1/3 border-b border-gray-200">{k}</td>
                    <td className="px-5 py-3 text-sm border-b border-gray-200">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      <EMICalculator defaultPrice={p.price || 1500000} productTitle={p.title} />

      <Dialog open={brochureOpen} onOpenChange={setBrochureOpen}>
        <DialogContent className="max-w-md" data-testid="brochure-gating-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">
              {settings?.brochure_gating_title || "Get the Brochure"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 -mt-2">
            {settings?.brochure_gating_subtitle || "Share your details — we'll open the brochure and have an expert call you within 2 hours."}
          </p>
          <form onSubmit={submitBrochureLead} className="space-y-3 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Your Name *</label>
              <input required className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={brochureForm.name} onChange={(e) => setBrochureForm({ ...brochureForm, name: e.target.value })} data-testid="brochure-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Mobile / WhatsApp Number *</label>
              <input required type="tel" pattern="[0-9 +\\-]{7,15}" className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={brochureForm.phone} onChange={(e) => setBrochureForm({ ...brochureForm, phone: e.target.value })} data-testid="brochure-phone" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Email (optional)</label>
              <input type="email" className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={brochureForm.email} onChange={(e) => setBrochureForm({ ...brochureForm, email: e.target.value })} data-testid="brochure-email" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setBrochureOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
              <button type="submit" disabled={brochureSubmitting} className="btn-primary text-sm" data-testid="brochure-submit">
                {brochureSubmitting ? "Opening…" : <><Download className="w-4 h-4" /> Open Brochure</>}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={inquireOpen} onOpenChange={setInquireOpen}>
        <DialogContent className="max-w-lg" data-testid="inquire-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">Inquire about<br /><span className="text-[#E65C00]">{p.title}</span></DialogTitle>
          </DialogHeader>
          <form onSubmit={submitInquiry} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Name</label>
                <input required className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inquire-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Phone</label>
                <input required className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="inquire-phone" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Email</label>
              <input required type="email" className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="inquire-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Brand of Interest</label>
              <select className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm bg-white" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="inquire-brand">
                <option value={p.brand}>{p.brand} (this product)</option>
                {(meta.brands || []).filter((b) => b !== p.brand).map((b) => <option key={b} value={b}>{b}</option>)}
                <option value="Open to suggestions">Open to suggestions</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Anything specific?</label>
              <textarea rows={3} className="mt-2 w-full px-3 py-2.5 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm resize-none" placeholder="e.g. EMI options, delivery to Patna, used vs new..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inquire-message" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setInquireOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary text-sm" data-testid="inquire-submit">
                {submitting ? "Sending..." : <><Send className="w-4 h-4" /> Send Inquiry</>}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <MediaLightbox items={gallery} open={lightboxOpen} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} />
    </>
  );
}
